const ENTRY_SEPARATOR = ","
const FIELD_SEPARATOR = "|"

export const METRIC_VALUE_FILTER_OPERATORS = [
  ">",
  "<",
  "=",
  ">=",
  "<=",
  "!=",
  "exists",
  "range",
] as const

export const NUMERIC_METRIC_VALUE_FILTER_OPERATORS = [
  "=",
  "!=",
  ">",
  ">=",
  "<",
  "<=",
  "range",
] as const

export const STRING_METRIC_VALUE_FILTER_OPERATORS = ["=", "!="] as const

export type MetricValueFilterOperator =
  (typeof METRIC_VALUE_FILTER_OPERATORS)[number]

export type MetricValueFilterValueType = "number" | "string"

export interface MetricValueFilter {
  // Shared between simulated and uploaded conversations, so this holds whichever
  // identifier the caller uses to resolve the metric (record id for simulated
  // conversations, metric name elsewhere).
  metricName: string
  operator: MetricValueFilterOperator
  value: number | string | null
  rangeMax?: number
  valueType: MetricValueFilterValueType
}

function encodePart(value: string): string {
  return value.replace(/%/g, "%25").replace(/\|/g, "%7C").replace(/,/g, "%2C")
}

function decodePart(value: string): string {
  return value.replace(/%2C/gi, ",").replace(/%7C/gi, "|").replace(/%25/g, "%")
}

export function metricValueFilterEquals(
  left: MetricValueFilter,
  right: MetricValueFilter,
): boolean {
  return (
    left.metricName === right.metricName &&
    left.operator === right.operator &&
    left.value === right.value &&
    left.rangeMax === right.rangeMax &&
    left.valueType === right.valueType
  )
}

export function getMetricValueFilterKey(filter: MetricValueFilter): string {
  return serializeMetricValueFilters([filter])
}

export function formatMetricValueFilterLabel(
  filter: MetricValueFilter,
  metricLabel: string,
): string {
  if (filter.operator === "exists") {
    return `${metricLabel} exists`
  }

  if (filter.operator === "range") {
    if (filter.rangeMax == null) {
      return `${metricLabel} >= ${filter.value}`
    }

    return `${metricLabel} between ${filter.value} and ${filter.rangeMax}`
  }

  return `${metricLabel} ${filter.operator} ${filter.value}`
}

export function serializeMetricValueFilters(
  filters: MetricValueFilter[],
): string {
  if (filters.length === 0) {
    return ""
  }

  return filters
    .map((filter) =>
      [
        encodePart(filter.metricName),
        encodePart(filter.operator),
        encodePart(filter.value == null ? "" : String(filter.value)),
        filter.valueType,
        typeof filter.rangeMax === "number" ? String(filter.rangeMax) : "",
      ].join(FIELD_SEPARATOR),
    )
    .join(ENTRY_SEPARATOR)
}

export function parseMetricValueFilters(raw: string): MetricValueFilter[] {
  if (!raw) {
    return []
  }

  const validOperators = new Set<string>(METRIC_VALUE_FILTER_OPERATORS)
  const filters: MetricValueFilter[] = []
  const seenKeys = new Set<string>()

  const pushUniqueFilter = (filter: MetricValueFilter) => {
    const key = getMetricValueFilterKey(filter)
    if (seenKeys.has(key)) {
      return
    }

    seenKeys.add(key)
    filters.push(filter)
  }

  for (const entry of raw.split(ENTRY_SEPARATOR)) {
    const parts = entry.split(FIELD_SEPARATOR)

    if (parts.length !== 5) {
      continue
    }

    const [metricNameRaw, operatorRaw, valueRaw, valueType, rangeMaxRaw] = parts
    const metricName = decodePart(metricNameRaw)
    const operator = decodePart(operatorRaw)

    if (
      !metricName ||
      !validOperators.has(operator) ||
      (valueType !== "number" && valueType !== "string")
    ) {
      continue
    }

    const decodedValue = decodePart(valueRaw)
    const decodedRangeMax = decodePart(rangeMaxRaw)

    if (valueType === "number") {
      const value = operator === "exists" ? null : Number(decodedValue)
      const rangeMax =
        decodedRangeMax === "" ? undefined : Number(decodedRangeMax)

      if (operator !== "exists" && !Number.isFinite(value)) {
        continue
      }

      if (operator === "range" && !Number.isFinite(rangeMax)) {
        continue
      }

      pushUniqueFilter({
        metricName,
        operator: operator as MetricValueFilterOperator,
        value,
        rangeMax,
        valueType,
      })
      continue
    }

    if (operator === "exists") {
      pushUniqueFilter({
        metricName,
        operator: operator as MetricValueFilterOperator,
        value: null,
        valueType,
      })
      continue
    }

    if (!decodedValue) {
      continue
    }

    pushUniqueFilter({
      metricName,
      operator: operator as MetricValueFilterOperator,
      value: decodedValue,
      valueType,
    })
  }

  return filters
}
